import { useEffect, useMemo, useState } from 'react'

import MiniSearch from 'minisearch'

import { notificationApi1, partnerApi2, userDetailsApi1 } from '@subaio/api-specs'
import { Form } from '@subaio/components/src/HookForm'
import { isNonNullable } from '@subaio/utils'

import type { Container as PageContainer } from 'lib/container'
import { getContainers } from 'lib/containers'

import { Search } from 'components/FormFields'

import { Container } from './Container'
import { getApiEntries } from './getApiEntries'
import { Item, type ItemProps } from './Item'
import { Menu } from './Menu'
import { SearchFieldContainer } from './SearchFieldContainer'
import { Subtitle } from './Subtitle'

type Props = {
	onClose: () => void
}

export const SearchMenu = ({ onClose }: Props) => {
	const containers = getContainers<PageContainer>()
	const documents = Object.entries(containers)
		.filter(([key]) => key.includes('Docs/'))
		.map(
			([key, value]): ItemProps => ({
				title: value.title as string,
				type: 'Document',
				description: value ? <p>{value.description}</p> : undefined,
				source: key,
			}),
		)

	const entries = useMemo(
		() =>
			[
				...documents,
				...getApiEntries(partnerApi2, 'Apis/PartnerApi/v2'),
				...getApiEntries(notificationApi1, 'Apis/Notifications/v1'),
				...getApiEntries(userDetailsApi1, 'Apis/UserDetails/v1'),
			].map((entry, index) => ({ ...entry, id: index })),
		[documents],
	)

	const miniSearch = new MiniSearch<ItemProps>({
		fields: ['title', 'description', 'url', 'attributes'],
		storeFields: ['type'],
		searchOptions: {
			fuzzy: 0.3,
			prefix: true,
			boost: { title: 2, description: 1, url: 0.5, attributes: 0.3 },
			boostDocument: (documentId, term, storedFields) => {
				if (storedFields?.type === 'Document') return 2
				return 1
			},
		},
	})
	miniSearch.addAll(entries)
	const [searchResults, setSearchResults] = useState<Array<ItemProps>>([])

	useEffect(() => {
		const listener = (event: KeyboardEvent) => {
			if (event.code === 'Escape') onClose()
		}
		window.addEventListener('keyup', listener)

		return window.removeEventListener('keydown', listener)
	}, [onClose])

	return (
		<Container>
			<SearchFieldContainer>
				<Form<{ search: string }>
					render={() => (
						<Search label='' placeholder='Search the docs' showOptionalHint={false} name='search' shouldAutofocus />
					)}
					onSubmit={() => undefined}
					onChange={({ search }) => {
						if (search) {
							const results = miniSearch.search(search)
							setSearchResults(
								results.map((result) => entries.find(({ id }) => result.id === id)).filter(isNonNullable),
							)
						} else setSearchResults([])
					}}
				/>
			</SearchFieldContainer>
			<Menu>
				{searchResults.length > 0 ? (
					searchResults.map((item, index) => <Item {...item} key={index} onClose={onClose} />)
				) : (
					<>
						<Subtitle>Frequently visited:</Subtitle>
						<Item
							title='Partner API documentation'
							source='Apis/PartnerApi/v2'
							type='ApiDocument'
							isSimple
							onClose={onClose}
						/>
						<Item title='Quick start guide' source='Docs/QuickStart' type='Document' isSimple onClose={onClose} />
						<Item title='Fintech Bridges' source='Docs/Bridges' type='Document' isSimple onClose={onClose} />
						<Item
							title='Submit transactions through the Partner API'
							source='Docs/Transactions/PartnerApi'
							type='Document'
							isSimple
							onClose={onClose}
						/>
						<Item
							title='Initiate PSD2 login flow'
							source='Apis/PartnerApi/v2'
							fragment='psd-2-create-initiate-psd-2'
							type='ApiEndpoint'
							method='post'
							url='/partner/{partnerId}/2.0.0/psd2/initiate'
							attributes={[]}
							isSimple
							onClose={onClose}
						/>
					</>
				)}
			</Menu>
		</Container>
	)
}
